import { AppConfig, SettingsConfig } from '@core/utils/config';
import { CompactChecklistTemplateResponse } from '@business/entities/checklist';
import { CompactDealBoardResponse } from '@business/entities/deal';
import { BoardResponse } from '@business/entities/project';
import { CompactChannelResponse } from '@business/entities/SupportChannel';
import { CommonService } from '@business/services';
import { FormSelectItem } from '@core/components/form';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { R } from '@core/utils/r';
import { CompactTMBoardResponse } from '@business/entities/tm/TimeMaterialBoardResponse';
import { ARDashboardResponse } from '@business/entities/advancedReporting';
import { VeryCompactDashboardResponse } from '@business/entities/dashboard';
import { CompactManagementFormTemplateResponse, ManagementFormTemplateResponse } from '@business/entities/managementFormTemplate';

export type GCModule =
  | 'contacts'
  | 'deals'
  | 'deal-board'
  | 'tm-board'
  | 'tm-pos'
  | 'tm-rma'
  | 'tm-pm'
  | 'tm-changeorders'
  | 'tm-invoice-discount'
  | 'sale-rep-override'
  | 'tm-labor'
  | 'people'
  | 'organizations'
  | 'projects'
  | 'project-board'
  | 'template'
  | 'support'
  | 'orders'
  | 'ticketing'
  | 'categories'
  | 'marketing'
  | 'marketing-board'
  | 'marketing-checklists'
  | 'reports'
  | 'reports-billing'
  | 'reports-contacts'
  | 'reports-projects'
  | 'reports-sales'
  | 'reports-support'
  | 'reports-time-material'
  | 'reports-users'
  | 'reports-subscriptions'
  | 'companies'
  | 'users'
  | 'teams'
  | 'settings'
  | 'activity-logs'
  | 'reviews'
  | 'todos'
  | 'checklists'
  | 'checklist-template'
  | 'library'
  | 'glossary'
  | 'order'
  | 'time'
  | 'orders-pos'
  | 'orders-invoices'
  | 'orders-shipments'
  | 'help-articles'
  | 'dashboards'
  | 'commissions'
  | 'courses'
  | 'proposals'
  | 'proposals-items'
  | 'proposal-templates'
  | 'timeMaterial'
  | 'ar-dashboard'
  | 'advanced-reporting'
  | 'projectDeadline'
  | 'change-default-payment'
  | 'dashboards-view'
  | 'dashboards-create'
  | 'tools'
  | 'presentations'
  | 'management-form-templates'
  | 'forms-shared-library';

export interface GCPermission {
  key: string;
  title: string;
  description?: string;
  module: GCModule;
  resourceId?: string | number;
  children?: GCPermission[];
  detailed?: boolean;
  defaultValue?: 'all' | 'me' | 'none';
  value?: boolean;
  ids?: string[];
  items?: FormSelectItem<any>[];
  isSingle?: boolean;
  /** When no permissions row exists yet, render this permission as granted instead of denied. */
  defaultOn?: boolean;
}

function createPermissionItem<T>(items: T[], id: keyof T, title: keyof T, module: GCModule): GCPermission[] {
  return items.map(item => ({ title: R.toString(item[title]), module, key: `${module}-${item[id]}`, resourceId: R.toString(item[id]) }));
}

export enum GCModuleEnum {
  contacts = 'contacts',
  organizations = 'organizations',
  deals = 'deals',
  projects = 'projects',
  projectBoard = 'project-board',
  projectTemplate = 'template',
  support = 'support',
  orders = 'orders',
  ordersInvoices = 'orders-invoices',
  ordersShipments = 'orders-shipments',
  ordersPos = 'orders-pos',
  tmPos = 'tm-pos',
  tmLabor = 'tm-labor',
  tmPM = 'tm-pm',
  tmChangeOrders = 'tm-changeorders',
  tmInvoiceDiscount = 'tm-invoice-discount',
  saleRepOverride = 'sale-rep-override',
  tmRMA = 'tm-rma',
  ticketing = 'ticketing',
  categories = 'categories',
  marketing = 'marketing',
  marketingBoard = 'marketing-board',
  marketingChecklists = 'marketing-checklists',
  reports = 'reports',
  reportBilling = 'reports-billing',
  reportContacts = 'reports-contacts',
  reportProjects = 'reports-projects',
  reportSales = 'reports-sales',
  reportSupport = 'reports-support',
  reportsTm = 'reports-time-material',
  reportUsers = 'reports-users',
  reportSubscriptions = 'reports-subscriptions',
  companies = 'companies',
  users = 'users',
  activityLogs = 'activity-logs',
  teams = 'teams',
  settings = 'settings',
  helpArticles = 'help-articles',
  reviews = 'reviews',
  people = 'people',
  todo = 'todos',
  checklists = 'checklists',
  library = 'library',
  dashboards = 'dashboards',
  commissions = 'commissions',
  glossary = 'glossary',
  proposals = 'proposals',
  proposalTemplates = 'proposal-templates',
  proposalItems = 'proposal-items',
  timeMaterial = 'timeMaterial',
  projectDeadline = 'projectDeadline',
  advancedReporting = 'advanced-reporting',
  dashboardView = 'dashboards-view',
  dashboardCreate = 'dashboards-create',
  tools = 'tools',
  presentations = 'presentations',
  managementFormTemplates = 'management-form-templates',
  formSharedLibrary = 'forms-shared-library',
}

export const generatePermissions = (
  dealBoards: CompactDealBoardResponse[],
  projectBoards: BoardResponse[],
  tmBoards: CompactTMBoardResponse[],
  channels: CompactChannelResponse[],
  templates: CompactChecklistTemplateResponse[],
  isOrders = false,
  isTM = false,
  dashboards: ARDashboardResponse[] = [],
  nDashboards: VeryCompactDashboardResponse[] = [],
  formTemplates: CompactManagementFormTemplateResponse[] = [],
) => {
  const items: GCPermission[] = R.compact([
    {
      title: 'Dashboards',
      module: 'dashboards',
      key: 'dashboards',
      detailed: true,
      children: [
        {
          title: 'All Dashboards',
          key: 'dashboards-view',
          module: 'dashboards-view',
          detailed: true,
          items: CommonService.selectItems(
            [
              {
                name: 'manage',
                value: 'View & Manage All',
              },
              {
                name: 'view',
                value: 'View All',
              },
              {
                name: 'selected',
                value: 'View and/or Manage Specific Items',
              },
            ],
            'value',
            'name',
            true,
          ),
          children: createPermissionItem(nDashboards, 'id', 'title', 'dashboards-view'),
        },
        { title: 'Create Dashboard', module: 'dashboards-create', key: 'dashboards-create' },
      ],
    },
    {
      title: gcLabel('Sales'),
      module: 'contacts',
      key: 'contacts',
      detailed: true,
      children: [
        {
          title: gcLabel('Organizations'),
          key: 'organizations',
          module: 'organizations',
          children: [
            {
              title: 'Communications',
              key: 'communications',
              module: 'organizations',
            },
            {
              title: 'Billing',
              key: 'billing',
              module: 'organizations',
            },
            {
              title: 'Allow to change Salesperson on existing subscriptions',
              key: 'allowChangeSalesperson',
              module: 'organizations',
            },
            ...(CommonService.isVipRestrictedAccount()
              ? [
                  {
                    title: 'View VIP Sensitive Information',
                    description: 'View phone, email, address and work orders for organizations marked VIP.',
                    key: 'vip',
                    module: 'organizations' as GCModule,
                    defaultOn: true,
                  },
                ]
              : []),
            {
              title: 'Files',
              key: 'files',
              module: 'organizations',
              detailed: true,
              isSingle: true,
              items: CommonService.selectItems(
                [
                  {
                    name: 'manage',
                    value: 'View & Manage All',
                  },
                  {
                    name: 'view',
                    value: 'View All',
                  },
                  {
                    name: 'selected',
                    value: 'View and/or Manage Specific Items',
                  },
                ],
                'value',
                'name',
                true,
              ),
            },
            {
              title: 'Transactions',
              key: 'transactions',
              module: 'organizations',
              detailed: true,
              isSingle: true,
              items: CommonService.selectItems(
                [
                  {
                    name: 'manage',
                    value: 'View & Manage All',
                  },
                  {
                    name: 'view',
                    value: 'View All',
                  },
                ],
                'value',
                'name',
                true,
              ),
            },
          ],
        },
        {
          title: gcLabel('Deals'),
          key: 'deals',
          module: 'deals',
          children: createPermissionItem(dealBoards, 'id', 'name', 'deals'),
        },
        { title: 'Contact', key: 'people', module: 'people' },
        {
          title: 'Proposals',
          module: 'proposals',
          key: 'proposals',
          children: [
            { title: 'Proposals', module: 'proposals-items', key: 'proposals-items', detailed: true },
            {
              title: 'Templates',
              module: 'proposal-templates',
              key: 'proposal-templates',
              isSingle: true,
              detailed: true,
              items: CommonService.selectItems(
                [
                  {
                    name: 'manage',
                    value: 'View & Manage All',
                  },
                  {
                    name: 'view',
                    value: 'View All',
                  },
                ],
                'value',
                'name',
                true,
              ),
            },
            { title: 'Allow To Modify Fees On Orders', module: 'proposals-items', key: 'modifyFeeInOrders' },
            ...(CommonService.isCanadaFirst()
              ? [{ title: 'Override SDR/BDR on Proposals & Work Orders', module: 'sale-rep-override', key: 'sale-rep-override' } as any]
              : []),
            {
              title: 'Allow users to change default payment collection terms',
              module: 'change-default-payment',
              key: 'changeDefaultPayment',
            },
          ],
        },
        {
          title: 'Presentation',
          module: 'presentation',
          key: 'presentation',
          children: [{ title: 'Allow sending presentations', module: 'send-presentation', key: 'send-presentation', value: true }],
        },
      ],
    },
    {
      title: gcLabel('Projects'),
      module: 'projects',
      key: 'projects',
      detailed: true,
      children: [
        { title: 'Board', module: 'project-board', key: 'project-board', children: createPermissionItem(projectBoards, 'id', 'name', 'projects') },
        { title: 'Templates', module: 'template', key: 'template' },
        { title: 'Deadlines', module: 'projectDeadline', key: 'projectDeadline' },
      ],
    },
    {
      title: gcLabel('Support'),
      module: 'support',
      key: 'support',
      children: [
        { title: 'Ticketing', module: 'ticketing', key: 'ticketing', children: createPermissionItem(channels, 'id', 'label', 'ticketing') },
        { title: 'Categories', module: 'categories', key: 'categories' },
      ],
    },
    {
      title: gcLabel('Subscriptions'),
      module: 'marketing',
      key: 'marketing',
      detailed: true,
      children: [
        { title: 'Board', module: 'marketing-board', key: 'marketing-board' },
        { title: 'Checklists', module: 'marketing-checklists', key: 'marketing-checklists' },
      ],
    },
    {
      title: gcLabel('Reports'),
      module: 'reports',
      key: 'reports',
      //detailed: true,
      children: R.compact([
        { title: 'Billing', module: 'reports-billing', key: 'reports-billing', detailed: true },
        { title: `Contacts`, module: 'reports-contacts', key: 'reports-contacts', detailed: true },
        { title: `${gcLabel('Projects')}`, module: 'reports-projects', key: 'reports-projects', detailed: true },
        { title: `Sales`, module: 'reports-sales', key: 'reports-sales', detailed: true },
        { title: `${gcLabel('Support')}`, module: 'reports-support', key: 'reports-support', detailed: true },
        { title: `${gcLabel('Time & Materials')}`, module: 'reports-time-material', key: 'reports-time-material', detailed: true },
        { title: `Users`, module: 'reports-users', key: 'reports-users', detailed: true },
        { title: `Subscriptions`, module: 'reports-subscriptions', key: 'reports-subscriptions', detailed: true },
        SettingsConfig.current()?.accountSettings.advancedReporting && {
          title: 'Advanced Reporting',
          module: 'advanced-reporting',
          key: 'advanced-reporting',
          description: 'Only admins can edit the reporting dashboards. Checking the box next to a specific dashboard will allow View permissions.',
          children: createPermissionItem(dashboards, 'id', 'title', 'ar-dashboard'),
        },
      ]),
    },
    {
      title: 'Reviews',
      module: 'reviews',
      key: 'reviews',
      //detailed: true,
      children: [],
    },
    { title: 'Manage Companies', module: 'companies', key: 'companies' },
    { title: 'Users', module: 'users', key: 'users' },
    { title: 'Activity Logs', module: 'activity-logs', key: 'activity-logs', detailed: true },
    { title: 'Teams', module: 'teams', key: 'teams' },
    { title: 'Settings', module: 'settings', key: 'settings' },
    // { title: 'Help Articles', module: 'help-articles', key: 'help-articles' },
    { title: 'To-Dos', module: 'todos', key: 'todos', detailed: true },
    {
      title: 'Checklists',
      module: 'checklists',
      key: 'checklists',
      detailed: true,
    },
    // {
    //   title: 'Checklist - Templates',
    //   module: 'checklist-template',
    //   key: 'checklist-template',
    //   children: createPermissionItem(templates, 'id', 'title', 'checklist-template'),
    // },
    {
      title: 'Checklist - Templates',
      module: 'checklist-template',
      key: 'checklist-template-new',
      detailed: true,
      isSingle: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
        ],
        'value',
        'name',
        true,
      ),
    },
    {
      title: 'Courses',
      module: 'courses',
      key: 'courses',
      detailed: true,
      isSingle: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
        ],
        'value',
        'name',
        true,
      ),
    },
    {
      title: 'Library',
      module: 'library',
      key: 'library',
      detailed: true,
      isSingle: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
          {
            name: 'selected',
            value: 'View and/or Manage Specific Items',
          },
        ],
        'value',
        'name',
        true,
      ),
    },
    {
      title: 'Forms - Shared Folders',
      module: 'forms-shared-library',
      key: 'forms-shared-library',
      detailed: true,
      isSingle: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
          {
            name: 'selected',
            value: 'View and/or Manage Specific Items',
          },
        ],
        'value',
        'name',
        true,
      ),
    },
    {
      title: 'Glossary',
      module: 'glossary',
      key: 'glossary',
      isSingle: true,
      detailed: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
        ],
        'value',
        'name',
        true,
      ),
    },

    {
      title: 'Commissions',
      module: 'commissions',
      key: 'commissions',
      detailed: true,
      defaultValue: 'me',
    },
    {
      title: 'Management Forms',
      module: 'management-form-templates',
      key: 'managementFormTemplates',
      detailed: true,
      children: createPermissionItem(formTemplates, 'id', 'title', 'management-form-templates'),
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Send All',
          },
          {
            name: 'view',
            value: 'View All',
          },
          {
            name: 'selected',
            value: 'View and/or Send Specific Items',
          },
        ],
        'value',
        'name',
        true,
      ),
    },
    isOrders
      ? {
          title: 'Orders',
          module: 'orders',
          key: 'orders',
          children: [
            { title: 'Manage POs', module: 'orders-pos', key: 'orders-pos' },
            { title: 'Manage Invoices', module: 'orders-invoices', key: 'orders-invoices' },
            { title: 'Manage Shipments', module: 'orders-shipments', key: 'orders-shipments' },
          ],
        }
      : null,

    isTM
      ? {
          title: 'Time and Materials',
          module: 'timeMaterial',
          key: 'timeMaterial',
          detailed: true,
          children: [
            {
              title: 'Board',
              module: 'tm-board',
              key: 'tm-board',
              children: createPermissionItem(
                tmBoards.filter(o => R.toString(o.id) !== '9999'),
                'id',
                'name',
                'timeMaterial',
              ),
            },
            { title: 'Manage POs', module: 'tm-pos', key: 'tm-pos' },
            { title: 'Manage PM', module: 'tm-pm', key: 'tm-pm' },
            { title: 'Manage Labor', module: 'tm-labor', key: 'tm-labor' },
            { title: 'Manage Change Orders', module: 'tm-changeorders', key: 'tm-changeorders' },
            { title: 'Adjust Invoice Discount', module: 'tm-invoice-discount', key: 'tm-invoice-discount' },
            { title: 'Manage RMA', module: 'tm-rma', key: 'tm-rma' },
          ],
        }
      : null,
  ]);
  return items;
};
